import { supabase } from './supabaseClient';
import type {
  Squad,
  SquadMember,
  SquadMemberKPI,
  KPIDefinition,
  Problem,
  SquadProblemSelection,
  CustomerMeeting,
  LetterOfIntent
} from '@/types/forge';

export const forgeApi = {
  // Squad operations
  async getSquad(squadId: string) {
    const { data, error } = await supabase
      .from('squads')
      .select('*')
      .eq('id', squadId)
      .single();
    
    return { data: data as Squad | null, error };
  },

  async getUserSquad(userId: string) {
    const { data, error } = await supabase
      .from('squad_members')
      .select(`
        *,
        squad:squads(*)
      `)
      .eq('user_id', userId)
      .eq('is_active', true);
    
    return { data: data as (SquadMember & { squad: Squad })[], error };
  },

  async getSquadMembers(squadId: string) {
    const { data, error } = await supabase
      .from('squad_members')
      .select(`
        *,
        user:users(id, email, full_name, avatar_url)
      `)
      .eq('squad_id', squadId)
      .eq('is_active', true);
    
    return { data: data as SquadMember[], error };
  },

  // KPI operations
  async getMemberKPIs(squadMemberId: string) {
    const { data, error } = await supabase
      .from('squad_member_kpis')
      .select(`
        *,
        definition:kpi_definition_id(*)
      `)
      .eq('squad_member_id', squadMemberId);
    
    return { data: data as SquadMemberKPI[], error };
  },

  async updateKPIProgress(kpiId: string, currentCount: number) {
    const { data, error } = await supabase
      .from('squad_member_kpis')
      .update({ 
        current_count: currentCount,
        status: currentCount > 0 ? 'in_progress' : 'not_started',
        updated_at: new Date().toISOString()
      })
      .eq('id', kpiId)
      .select();
    
    return { data, error };
  },

  async submitKPIProof(kpiId: string, proofUrls: string[], notes?: string) {
    const { data, error } = await supabase
      .from('squad_member_kpis')
      .update({
        proof_urls: proofUrls,
        notes: notes,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', kpiId)
      .select();
    
    return { data, error };
  },

  // Problem bank operations
  async getProblems(filters?: { industry?: string; difficulty?: string }) {
    let query = supabase
      .from('problem_bank')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (filters?.industry) {
      query = query.eq('industry', filters.industry);
    }
    if (filters?.difficulty) {
      query = query.eq('difficulty', filters.difficulty);
    }

    const { data, error } = await query;
    return { data: data as Problem[], error };
  },

  async getProblem(problemId: string) {
    // Increment view count
    await supabase
      .from('problem_bank')
      .update({ view_count: supabase.rpc('increment') })
      .eq('id', problemId);

    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('id', problemId)
      .single();
    
    return { data: data as Problem | null, error };
  },

  async selectProblem(squadId: string, problemId: string) {
    // First, check if already selected
    const { data: existing } = await supabase
      .from('squad_problem_selections')
      .select('*')
      .eq('squad_id', squadId)
      .eq('problem_id', problemId)
      .single();

    if (existing) {
      return { data: existing, error: null };
    }

    // Create new selection
    const { data, error } = await supabase
      .from('squad_problem_selections')
      .insert({
        squad_id: squadId,
        problem_id: problemId,
        status: 'shortlisted',
        selected_at: new Date().toISOString()
      })
      .select()
      .single();

    // Increment problem interest count
    if (!error) {
      await supabase
        .from('problem_bank')
        .update({ interest_count: supabase.rpc('increment') })
        .eq('id', problemId);
    }

    return { data, error };
  },

  async getSquadSelectedProblems(squadId: string) {
    const { data, error } = await supabase
      .from('squad_problem_selections')
      .select(`
        *,
        problem:problem_id(*)
      `)
      .eq('squad_id', squadId);
    
    return { data: data as (SquadProblemSelection & { problem: Problem })[], error };
  },

  // Customer meetings
  async addMeeting(meeting: Omit<CustomerMeeting, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('customer_meetings')
      .insert(meeting)
      .select()
      .single();
    
    return { data: data as CustomerMeeting | null, error };
  },

  async getSquadMeetings(squadId: string) {
    const { data, error } = await supabase
      .from('customer_meetings')
      .select('*')
      .eq('squad_id', squadId)
      .order('meeting_date', { ascending: false });
    
    return { data: data as CustomerMeeting[], error };
  },

  // LOI operations
  async addLOI(loi: Omit<LetterOfIntent, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('letters_of_intent')
      .insert(loi)
      .select()
      .single();
    
    return { data: data as LetterOfIntent | null, error };
  },

  async getSquadLOIs(squadId: string) {
    const { data, error } = await supabase
      .from('letters_of_intent')
      .select('*')
      .eq('squad_id', squadId)
      .order('created_at', { ascending: false });
    
    return { data: data as LetterOfIntent[], error };
  },

  async updateLOIStatus(loiId: string, status: LetterOfIntent['status']) {
    const updates: any = { status };
    
    if (status === 'signed') {
      updates.signed_at = new Date().toISOString();
    }
    
    const { data, error } = await supabase
      .from('letters_of_intent')
      .update(updates)
      .eq('id', loiId)
      .select()
      .single();
    
    return { data, error };
  }
};